
import { useState } from "react";
import { motion } from "framer-motion";
import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { useDealerNavigation } from "./useDealerNavigation";

interface DealerCardContainerProps {
  id: string;
  city?: string;
  className?: string;
  activated?: boolean;
  isHomePage?: boolean;
  isHorizontalLayout?: boolean;
  isModalOpen?: boolean;
  children: React.ReactNode;
}

export function DealerCardContainer({
  id,
  city,
  className,
  activated = false,
  isHomePage = false,
  isHorizontalLayout = false,
  isModalOpen = false,
  children
}: DealerCardContainerProps) {
  const { navigateToDealer } = useDealerNavigation();
  
  const handleCardClick = (e: React.MouseEvent) => {
    // Previeni la navigazione se una modale è aperta
    if (isModalOpen) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    
    navigateToDealer(id, city);
  };

  const isHorizontal = className?.includes('md:flex-row') || isHorizontalLayout;
  const isHome = className?.includes('card-metal-highlight') || isHomePage;

  // Animation variants for home page cards
  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.5 }
    },
    hover: {
      y: -8,
      boxShadow: "0 10px 25px rgba(0, 0, 0, 0.1)",
      transition: { duration: 0.3 }
    }
  };

  const CardComponent = isHome ? motion.div : 'div';
  const motionProps = isHome ? {
    variants: cardVariants,
    initial: "hidden",
    animate: "visible",
    whileHover: "hover"
  } : {};

  return (
    <CardComponent {...motionProps} className="w-full h-full flex">
      <Card 
        className={cn(
          "w-full transition-all duration-300 hover:shadow-xl border-metal-light/20 group overflow-hidden h-full flex flex-col",
          className,
          activated ? "card-metal-highlight" : "",
          !activated && "border-dashed opacity-90",
          isHome ? "bg-gradient-to-br from-background/95 to-metal-light/10 backdrop-blur-md home-dealer-card" : "",
          isHome && "bg-pattern-gold"
        )}
        onClick={handleCardClick}
      >
        <div className={cn(
          "flex flex-col w-full h-full",
          isHorizontal && "md:flex-row md:items-center"
        )}>
          {children}
        </div>
      </Card>
    </CardComponent>
  );
}
